// @ts-nocheck
/**
 * Overview Metrics Component
 * Displays key performance indicators in card format (styled like Trade Log page)
 */

import { Box } from "@chakra-ui/react";
import React, { useMemo, useState, useEffect } from "react";
import { TradeStatistics } from "../../../../types/trade";
import TradeLogMiniStatistics from "../../TradeLog/components/TradeLogMiniStatistics";
import SortableCard from "../../Dashboard/components/SortableCard";
import { useDashboardSummary } from "../../../../hooks/useTradeStatistics";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { useIsBelowLg, useIsMobile } from "@/hooks/use-mobile";
import { MINI_STAT_LOSS_GRADIENT } from "../../../../utils/miniStatGradient";
import {
  dashboardMiniStatsGridTemplateColumns,
  miniStatAmountNeedsFullWidthOnMobile,
  miniStatGridTemplateColumns,
} from "../../../../utils/miniStatGridLayout";

function formatLargestLossAmount(
  largestLoss: number,
  formatMoney: (value: number, options?: { showSign?: boolean }) => string,
): string {
  return largestLoss < 0 ? formatMoney(largestLoss) : "N/A";
}

interface OverviewMetricsProps {
  statistics: TradeStatistics;
  backendStats?: {
    portfolio_value: number | null;
    portfolio_change_percent: number | null;
    win_rate_change_percent: number | null;
    total_return_percent: number;
    average_return_percent: number;
    // Comparison fields
    pnl_change_percent: number | null;
    pnl_vs_previous_percent: number | null;
    average_return_change_percent: number | null;
    average_return_vs_previous_percent: number | null;
    total_trades_change_percent: number | null;
    total_trades_vs_previous_percent: number | null;
    closed_trades_change_percent: number | null;
    closed_trades_vs_previous_percent: number | null;
  } | null;
  comparisonType?: "period-over-period" | "within-period";
  brokerAccountCode?: string;
  activeBrokerName?: string | null;
  supportsPortfolioBalanceFromBroker?: boolean;
  /** From GET /broker/capabilities — when false, overview strip matches trade-based metrics (with Expectancy vs Balance rules). */
  supportsPortfolioGrowth?: boolean;
  expectancyValue?: number | null;
  /** Fixed layout without drag-and-drop (e.g. report overview metrics). */
  embedded?: boolean;
}

const OVERVIEW_METRICS_DEFAULT_CARD_ORDER = [
  "totalPnL",
  "winRate",
  "totalTrades",
  "portfolioValue",
  "averageReturn",
  "closedTrades",
  "largestWin",
  "largestLoss",
] as const;

const OverviewMetrics: React.FC<OverviewMetricsProps> = ({
  statistics,
  backendStats,
  comparisonType = "period-over-period",
  loading = false,
  brokerAccountCode,
  activeBrokerName,
  supportsPortfolioBalanceFromBroker = true,
  supportsPortfolioGrowth,
  expectancyValue = null,
  embedded = false,
}) => {
  const { formatMoney } = useDisplayCurrency();
  const isMobile = useIsMobile();
  const isBelowLg = useIsBelowLg();

  const defaultCardOrder = [...OVERVIEW_METRICS_DEFAULT_CARD_ORDER];

  const normalizeOverviewCardOrder = (order: string[]): string[] => {
    const validIds = new Set([...defaultCardOrder, "activeTrades"]);
    let ids = order.filter((id) => validIds.has(id));
    ids = ids.map((id) => (id === "activeTrades" ? "largestLoss" : id));

    const deduped: string[] = [];
    const seen = new Set<string>();
    for (const id of ids) {
      if (seen.has(id)) continue;
      seen.add(id);
      deduped.push(id);
    }

    if (!deduped.includes("largestLoss")) {
      const winIdx = deduped.indexOf("largestWin");
      if (winIdx >= 0) deduped.splice(winIdx + 1, 0, "largestLoss");
      else deduped.push("largestLoss");
    } else {
      const winIdx = deduped.indexOf("largestWin");
      const lossIdx = deduped.indexOf("largestLoss");
      if (winIdx >= 0 && lossIdx >= 0 && lossIdx !== winIdx + 1) {
        deduped.splice(lossIdx, 1);
        deduped.splice(winIdx + 1, 0, "largestLoss");
      }
    }

    for (const id of defaultCardOrder) {
      if (!deduped.includes(id)) deduped.push(id);
    }

    return deduped.filter((id) => defaultCardOrder.includes(id));
  };

  const [cardOrder, setCardOrder] = useState<string[]>(() => {
    if (embedded) return defaultCardOrder;
    const saved = localStorage.getItem("statistics_card_order");
    if (saved) {
      try {
        return normalizeOverviewCardOrder(JSON.parse(saved));
      } catch {
        return defaultCardOrder;
      }
    }
    return defaultCardOrder;
  });

  useEffect(() => {
    if (embedded) return;
    const normalized = normalizeOverviewCardOrder(cardOrder);
    if (
      normalized.length !== cardOrder.length ||
      normalized.some((id, index) => id !== cardOrder[index])
    ) {
      setCardOrder(normalized);
      return;
    }
    localStorage.setItem("statistics_card_order", JSON.stringify(cardOrder));
  }, [cardOrder, embedded]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCardOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Format percentage
  const formatPercent = (value: number): string => {
    return `${value.toFixed(2)}%`;
  };

  // Portfolio value: prefer /trades/statistics; only hit dashboard summary as fallback.
  const needsDashboardSummaryFallback =
    Boolean(brokerAccountCode?.trim()) &&
    (backendStats?.portfolio_value === null || backendStats?.portfolio_value === undefined);

  const dashboardSummaryTimeframes = useMemo(() => ({
    portfolio: "all-time" as const,
  }), []);
  
  const { data: dashboardSummary } = useDashboardSummary(
    brokerAccountCode || "",
    dashboardSummaryTimeframes,
    activeBrokerName,
    { enabled: needsDashboardSummaryFallback }
  );

  // Portfolio value from backend (includes transfers) or fallback to dashboard summary
  const portfolioValue = useMemo(() => {
    if (backendStats?.portfolio_value !== null && backendStats?.portfolio_value !== undefined) {
      return backendStats.portfolio_value;
    }
    // Fallback: use actual portfolio value from dashboard summary (not mock $1000)
    if (dashboardSummary?.portfolio.portfolio_value !== null && dashboardSummary?.portfolio.portfolio_value !== undefined) {
      return dashboardSummary.portfolio.portfolio_value;
    }
    // Last resort: return 0 instead of mock data
    return 0;
  }, [backendStats?.portfolio_value, dashboardSummary?.portfolio.portfolio_value]);

  // Get percentage changes from backend data based on comparison type
  const getTotalPnLPercentage = (): number | null => {
    if (comparisonType === "period-over-period") {
      // Period-over-period: current vs previous period
      if (backendStats?.pnl_vs_previous_percent !== null && backendStats?.pnl_vs_previous_percent !== undefined) {
        return Number(backendStats.pnl_vs_previous_percent.toFixed(2));
      }
    } else {
      // Within-period: end vs beginning of current period
      if (backendStats?.pnl_change_percent !== null && backendStats?.pnl_change_percent !== undefined) {
        return Number(backendStats.pnl_change_percent.toFixed(2));
      }
    }
    // No comparison data available - return null (don't use total_return_percent as it's a different metric)
    return null;
  };

  const getTotalPnLTooltip = (): string => {
    const periodOverPeriod = backendStats?.pnl_vs_previous_percent !== null && backendStats?.pnl_vs_previous_percent !== undefined
      ? `${backendStats.pnl_vs_previous_percent >= 0 ? "+" : ""}${backendStats.pnl_vs_previous_percent.toFixed(2)}%`
      : "N/A";
    const withinPeriod = backendStats?.pnl_change_percent !== null && backendStats?.pnl_change_percent !== undefined
      ? `${backendStats.pnl_change_percent >= 0 ? "+" : ""}${backendStats.pnl_change_percent.toFixed(2)}%`
      : "N/A";
    const currentValue = comparisonType === "period-over-period" ? periodOverPeriod : withinPeriod;
    return `Total P&L change. Current view: ${comparisonType === "period-over-period" ? "vs previous period" : "within period"} (${currentValue}). Period-over-period: ${periodOverPeriod}. Within-period: ${withinPeriod}.`;
  };

  const getPortfolioValuePercentage = (): number | null => {
    // Portfolio Value: always use portfolio_change_percent (change in portfolio value)
    if (backendStats?.portfolio_change_percent !== null && backendStats?.portfolio_change_percent !== undefined) {
      return Number(backendStats.portfolio_change_percent.toFixed(2));
    }
    return null;
  };

  const getWinRatePercentage = (): number | null => {
    // Win Rate: always use win_rate_change_percent (change in win rate)
    if (backendStats?.win_rate_change_percent !== null && backendStats?.win_rate_change_percent !== undefined) {
      return Number(backendStats.win_rate_change_percent.toFixed(2));
    }
    return null;
  };

  const getAverageReturnPercentage = (): number | null => {
    if (comparisonType === "period-over-period") {
      // Period-over-period: current vs previous period
      if (backendStats?.average_return_vs_previous_percent !== null && backendStats?.average_return_vs_previous_percent !== undefined) {
        return Number(backendStats.average_return_vs_previous_percent.toFixed(2));
      }
    } else {
      // Within-period: end vs beginning of current period
      if (backendStats?.average_return_change_percent !== null && backendStats?.average_return_change_percent !== undefined) {
        return Number(backendStats.average_return_change_percent.toFixed(2));
      }
    }
    return null;
  };

  const getAverageReturnTooltip = (): string => {
    const periodOverPeriod = backendStats?.average_return_vs_previous_percent !== null && backendStats?.average_return_vs_previous_percent !== undefined
      ? `${backendStats.average_return_vs_previous_percent >= 0 ? "+" : ""}${backendStats.average_return_vs_previous_percent.toFixed(2)}%`
      : "N/A";
    const withinPeriod = backendStats?.average_return_change_percent !== null && backendStats?.average_return_change_percent !== undefined
      ? `${backendStats.average_return_change_percent >= 0 ? "+" : ""}${backendStats.average_return_change_percent.toFixed(2)}%`
      : "N/A";
    return `Average return change. Current view: ${comparisonType === "period-over-period" ? "vs previous period" : "within period"}. Period-over-period: ${periodOverPeriod}. Within-period: ${withinPeriod}.`;
  };

  const metricsMap = useMemo(() => ({
    totalPnL: {
      title: "Total P&L",
      amount: formatMoney(statistics.totalPnL),
      percentage: null,
      tooltip: getTotalPnLTooltip(),
      backgroundGradient: undefined as string | undefined,
      showPercentage: false,
    },
    winRate: {
      title: "Win Rate",
      amount: formatPercent(statistics.winRate),
      percentage: 0,
      tooltip: "The percentage of closed trades that resulted in a profit. Calculated as (Winning Trades / Total Closed Trades) × 100.",
      backgroundGradient: "linear-gradient(135deg, #38BBD1, #3EEAEC)",
      showPercentage: false,
    },
    totalTrades: {
      title: "Total Trades",
      amount: statistics.totalTrades.toString(),
      percentage: 0,
      tooltip: "The total number of trades you have executed, including both open and closed positions.",
      backgroundGradient: "linear-gradient(135deg, #38BBD1, #3EEAEC)",
      showPercentage: false,
    },
    portfolioValue: (() => {
      // Reports (embedded) always show Expectancy — window stats, not live broker NAV.
      const showExpectancyInsteadOfBalance =
        embedded ||
        supportsPortfolioGrowth === false ||
        !supportsPortfolioBalanceFromBroker;
      const expectancyAmount =
        expectancyValue ??
        backendStats?.average_per_trade_pnl ??
        null;
      return {
        title: showExpectancyInsteadOfBalance ? "Expectancy" : "Current Balance",
        amount: showExpectancyInsteadOfBalance
          ? formatMoney(expectancyAmount ?? 0, { showSign: true })
          : formatMoney(portfolioValue),
        percentage: 0,
        tooltip: showExpectancyInsteadOfBalance
          ? embedded
            ? "Average outcome per closed trade in this report’s analysis window."
            : supportsPortfolioGrowth === false
              ? "Average outcome per closed trade (aligned with statistics). Performance is based on closed trades / realized P&L — not portfolio NAV from deposits or live broker equity."
              : "Expected average outcome per trade from analytics. Shown when this broker does not sync live account balance for headline display."
          : "Current portfolio value including trades and transfers where your broker provides synced equity data.",
        backgroundGradient: undefined as string | undefined,
        showPercentage: false,
      };
    })(),
    averageReturn: {
      title: "Average Return",
      amount: formatPercent(statistics.averageReturnPercent),
      percentage: 0,
      tooltip: getAverageReturnTooltip(),
      backgroundGradient: undefined as string | undefined,
      showPercentage: false,
    },
    activeTrades: {
      title: "Active Trades",
      amount: `${statistics.openTrades} OPEN`,
      percentage: 0,
      backgroundGradient: "linear-gradient(135deg, #38BBD1, #3EEAEC)",
      showPercentage: false,
      tooltip: "The number of trades that are currently open and have not yet been closed. These positions are still active in the market.",
    },
    closedTrades: {
      title: "Closed Trades",
      amount: statistics.closedTrades.toString(),
      percentage: 0,
      tooltip: "The total number of trades that have been completed and closed. This includes both winning and losing trades.",
      backgroundGradient: "linear-gradient(135deg, #38BBD1, #3EEAEC)",
      showPercentage: false,
    },
    largestWin: {
      title: "Largest Win",
      amount: formatMoney(statistics.largestWin),
      percentage: 0,
      backgroundGradient: undefined as string | undefined,
      showPercentage: false,
      tooltip: "The largest profit from a single winning trade. This shows your best-performing trade.",
    },
    largestLoss: {
      title: "Largest Loss",
      amount: formatLargestLossAmount(statistics.largestLoss, formatMoney),
      percentage: null,
      backgroundGradient: MINI_STAT_LOSS_GRADIENT,
      showPercentage: false,
      tooltip: "The single largest loss from any closed trade. This represents your worst-performing trade.",
    },
  }), [
    statistics.totalPnL,
    statistics.winRate,
    statistics.totalTrades,
    statistics.openTrades,
    statistics.closedTrades,
    statistics.largestWin,
    statistics.largestLoss,
    statistics.averageReturnPercent,
    portfolioValue,
    backendStats?.portfolio_value,
    backendStats?.portfolio_change_percent,
    backendStats?.win_rate_change_percent,
    backendStats?.total_return_percent,
    backendStats?.pnl_change_percent,
    backendStats?.pnl_vs_previous_percent,
    backendStats?.average_return_change_percent,
    backendStats?.average_return_vs_previous_percent,
    comparisonType,
    getTotalPnLPercentage,
    getPortfolioValuePercentage,
    getWinRatePercentage,
    getAverageReturnPercentage,
    getTotalPnLTooltip,
    getAverageReturnTooltip,
    supportsPortfolioBalanceFromBroker,
    supportsPortfolioGrowth,
    expectancyValue,
    embedded,
  ]);

  const visibleMetrics = useMemo(() => {
    const order = embedded ? defaultCardOrder : cardOrder;
    return order
      .map((id) => {
        const metric = metricsMap[id as keyof typeof metricsMap];
        return metric ? { id, title: metric.title, amount: metric.amount } : null;
      })
      .filter(Boolean) as Array<{ id: string; title: string; amount: string }>;
  }, [cardOrder, defaultCardOrder, embedded, metricsMap]);

  const gridTemplateColumns = useMemo(() => {
    if (isMobile) {
      return dashboardMiniStatsGridTemplateColumns(visibleMetrics, {
        isMobile: true,
        maxColumns: 4,
        gapPx: 24,
      });
    }

    const needsFullWidth = visibleMetrics.some(
      (item) => item.amount && miniStatAmountNeedsFullWidthOnMobile(item.amount),
    );

    return miniStatGridTemplateColumns(visibleMetrics, {
      maxColumns: 4,
      minColumns: isBelowLg && !needsFullWidth ? 2 : undefined,
      gapPx: 24,
    });
  }, [visibleMetrics, isMobile, isBelowLg]);

  // Render card based on ID
  const renderCard = (cardId: string) => {
    const metric = metricsMap[cardId as keyof typeof metricsMap];
    if (!metric) return null;

    return (
      <TradeLogMiniStatistics
        title={metric.title}
        amount={metric.amount}
        percentage={metric.percentage}
        backgroundGradient={metric.backgroundGradient}
        showPercentage={metric.showPercentage}
        tooltip={metric.tooltip}
        fitAmount
      />
    );
  };

  const metricsGrid = (
    <Box
      as="div"
      w="100%"
      minW={0}
      display="grid"
      gridTemplateColumns={gridTemplateColumns}
      gap="24px"
      data-mini-stats-grid
      {...(embedded ? { "data-report-overview-metrics-grid": "" } : {})}
    >
      {(embedded ? defaultCardOrder : cardOrder).map((cardId) =>
        embedded ? (
          <Box key={cardId} w="100%" minW={0}>
            {renderCard(cardId)}
          </Box>
        ) : (
          <Box key={cardId} w="100%" minW={0}>
            <SortableCard id={cardId} items={cardOrder}>
              {renderCard(cardId)}
            </SortableCard>
          </Box>
        )
      )}
    </Box>
  );

  if (embedded) {
    return metricsGrid;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={cardOrder} strategy={rectSortingStrategy}>
        {metricsGrid}
      </SortableContext>
    </DndContext>
  );
};

export default OverviewMetrics;

